import tinycolor from 'tinycolor2';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useMapContainerResize } from './useMapContainerResize';
import { useSolarGroundOverlays } from './useSolarGroundOverlays';

import {
  computePanelCellGridLines,
  computePanelCorners,
  panelIndexAtPoint,
  SolarPanelPlacement,
  SolarRoofSegmentInfo,
} from './panelGeometry';

export {
  buildOrientedRectangle,
  capIncludedPanels,
  createRoofPackingFrame,
  createFacePanelFitPredicate,
  makeFrame,
  packFace,
  roofEdgeAnchorAzimuth,
  polygonAreaMeters2,
  ringTouchesPolygon,
} from '@payaca/helpers/solarFacePacker';
export type {
  FacePolygon as FacePackerPolygon,
  PackedFacePanel,
  PackFaceArgs,
  Vec2,
  LatLng,
} from '@payaca/helpers/solarFacePacker';
export {
  buildRoofTemplate,
  ROOF_TEMPLATE_CYCLE,
  ROOF_TEMPLATE_LABELS,
} from './roofTemplates';
export type {
  RoofDrawKind,
  RoofTemplateKind,
  RoofTemplateResult,
  TemplateVertexLink,
} from './roofTemplates';

import {
  buildOrientedRectangle,
  facesBoundingBox,
  packFace,
} from '@payaca/helpers/solarFacePacker';
import { resolveFaceChromeMode } from './faceChromeMode';
import { isPanelWithinRoof } from './isPanelWithinRoof';
export { isPanelWithinRoof } from './isPanelWithinRoof';
import { boundaryEditCommit } from './boundaryEditCommit';
import { snapPanelDrag } from './snapPanelDrag';
export { snapPanelDrag } from './snapPanelDrag';
import { buildRoofTemplate, type RoofDrawKind } from './roofTemplates';
import { roofOutlineAction } from './roofOutline';
import {
  BUILDING_OUTLINE_STYLE,
  dashedPolylineIcons,
  HIGHLIGHTED_STRING_OUTLINE_STYLE,
  SELECTED_PANELS_OUTLINE_STYLE,
  TITLE_BOUNDARY_STYLE,
} from './outlineStyles';
export {
  BUILDING_OUTLINE_STYLE,
  type DashedOutlineStyle,
  HIGHLIGHTED_STRING_OUTLINE_STYLE,
  SELECTED_PANELS_OUTLINE_STYLE,
  TITLE_BOUNDARY_STYLE,
} from './outlineStyles';

declare global {
  interface Window {
    google?: any;
    __payacaGoogleMapsLoading?: Promise<void>;
  }
}

const GOOGLE_MAPS_SCRIPT_ID = 'payaca-google-maps-js';

/** Our zoom ceiling, past the satellite imagery's own (~z20-21 in UK suburbs). */
const MAX_MAP_ZOOM = 23;
const BLANK_MAP_TYPE_ID = 'payaca-blank';

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.maps?.Map) return Promise.resolve();
  if (window.__payacaGoogleMapsLoading) return window.__payacaGoogleMapsLoading;

  window.__payacaGoogleMapsLoading = new Promise<void>((resolve, reject) => {
    // `loading=async`'s `load` event fires before the API is usable — Google's
    // pattern is a `callback` invoked once `google.maps` is ready.
    (window as any).__payacaGoogleMapsReady = () => resolve();
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      if (window.google?.maps?.Map) resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&loading=async&callback=__payacaGoogleMapsReady`;
    script.async = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps JS'));
    document.head.appendChild(script);
  });
  return window.__payacaGoogleMapsLoading;
};

export type SolarLatLng = { lat: number; lng: number };

export type SolarFacePolygon = {
  outer: SolarLatLng[];
  holes: SolarLatLng[][];
};

export type SolarPackedPanelPlacement = {
  corners: SolarLatLng[];
  faceIndex: number;
  orientation: string;
  annualAcKwh: number;
  onProperty?: boolean | null;
};

export type SolarFluxOverlayInfo = {
  /** Short-lived signed PNG URL — render what's given, the parent refetches. */
  url: string;
  /** Stable image identity across signed URL refreshes; change when pixels change. */
  resourceKey?: string;
  bounds: { sw: SolarLatLng; ne: SolarLatLng };
};

export type SolarRoofViewProps = {
  apiKey: string;
  centre: { lat: number; lng: number };
  panels: SolarPanelPlacement[];
  roofSegments: SolarRoofSegmentInfo[];
  panelWidthMeters: number;
  panelHeightMeters: number;
  /** Indexes refer to `packedPanels` when present, otherwise to `panels`. */
  activePanelsCount?: number;
  /** Included panels; use `highlightedPanelIndexes` for the editing selection. */
  selectedPanelIndexes?: number[];
  onPanelClick?: (
    panelIndex: number,
    modifiers?: { metaKey: boolean; ctrlKey: boolean; shiftKey?: boolean }
  ) => void;
  /** Editing selection, independent of included `selectedPanelIndexes`. */
  highlightedPanelIndexes?: number[];
  /** The selected or hovered string's panels, distinct from the editing selection. */
  stringHighlightPanelIndexes?: number[];
  /** The hovered string's panels, drawn fainter than the selected string. */
  stringHoverPanelIndexes?: number[];
  /** Per rendered panel index → string colour key (0-based, cycles a palette of 8); null/undefined = unassigned. */
  panelStringKeys?: (number | null)[];
  /** One label per string, drawn once near the first panel of the string. */
  stringLabels?: { panelIndex: number; label: string }[];
  /** Called with the label's panel index when its pill is clicked. */
  onStringLabelClick?: (panelIndex: number) => void;
  /** Called with the label's panel index while its pill is hovered, then null. */
  onStringLabelHover?: (panelIndex: number | null) => void;
  /** Frozen as a rigid group when a drag starts. */
  draggablePanelIndexes?: number[];
  snapPanelsToArray?: boolean;
  panelGapMeters?: number;
  columnGapMeters?: number;
  rowGapMeters?: number;
  /** Last applied per-face gaps, in the same packing frame as drag snapping. */
  snapPanelGaps?: Array<{ columnGapM: number; rowGapM: number } | undefined>;
  /** Applied array bearings indexed like faces, independent of corner order. */
  snapArrayAzimuthDegrees?: Array<number | null | undefined>;
  /** Applied flush roof planes, indexed like faces; frames use plan metres. */
  snapRoofPlanes?: Array<
    { pitchDegrees: number; azimuthDegrees: number } | undefined
  >;
  onPanelsMoved?: (
    delta: { dLat: number; dLng: number },
    removedPanelIndexes: number[]
  ) => void;
  faces?: SolarFacePolygon[];
  faceEditMode?: 'select';
  selectedFaceIndex?: number | null;
  /** Saved reference endpoints, shown even when an outline edit invalidated them. */
  referenceEdge?: [SolarLatLng, SolarLatLng] | null;
  showAllFaceOutlines?: boolean;
  grabFaces?: boolean;
  moveWholeRoof?: boolean;
  /** Fired with the whole-roof drag translation (degrees delta). */
  onRoofMoved?: (delta: { dLat: number; dLng: number }) => void;
  onFaceMoved?: (
    faceIndex: number,
    delta: { dLat: number; dLng: number }
  ) => void;
  onSelectFace?: (faceIndex: number) => void;
  obstructions?: SolarLatLng[][];
  obstacleEditMode?: boolean;
  onObstacleRemove?: (index: number) => void;
  selectedObstructionIndex?: number | null;
  addObstacleMode?: boolean;
  onObstacleDrawn?: (ring: SolarLatLng[]) => void;
  staticFaceOutlines?: boolean;
  /** When present, replaces panels computed from `panels` + `roofSegments`; selection indexes refer to this list. */
  packedPanels?: SolarPackedPanelPlacement[];
  fluxOverlay?: SolarFluxOverlayInfo;
  showFluxOverlay?: boolean;
  /** Rendered as a GroundOverlay under the design when `showRgbUnderlay`. */
  rgbUnderlay?: SolarFluxOverlayInfo;
  showRgbUnderlay?: boolean;
  /** Also draw the underlay below Google's satellite ceiling: only for geometry computed on the underlay itself. */
  underlayAtEveryZoom?: boolean;
  /** An overlay's signed URL failed to load (usually expired) — refetch fresh URLs. */
  onOverlayImageError?: () => void;
  editableFaces?: boolean;
  onFaceEdited?: (faceIndex: number, polygon: SolarFacePolygon) => void;
  boundaryEditFaceIndex?: number | null;
  /** Commits final geometry after vertex release; non-pointer path edits are debounced. */
  onFaceBoundaryChanged?: (
    faceIndex: number,
    polygon: SolarFacePolygon
  ) => void;
  addFaceMode?: boolean;
  addFaceAzimuthDegrees?: number;
  addFaceTemplate?: RoofDrawKind;
  onTemplateDrawn?: (result: ReturnType<typeof buildRoofTemplate>) => void;
  fitToFacesKey?: number;
  onFaceDrawn?: (polygon: SolarFacePolygon) => void;
  onFaceDrawStart?: (corner: SolarLatLng) => void;
  processingLabel?: string;
  repositionMode?: boolean;
  repositionCentre?: SolarLatLng;
  onRepositionChange?: (latLng: SolarLatLng) => void;
  markerCentre?: SolarLatLng;
  /** Attribute as "Building outlines © OpenStreetMap contributors" wherever shown. */
  footprints?: SolarLatLng[][];
  footprintPickMode?: boolean;
  onFootprintPick?: (ring: SolarLatLng[]) => void;
  /** HM Land Registry INSPIRE index polygon (title parcel); render requires the HMLR + OS attribution statements. */
  parcelBoundary?: SolarLatLng[] | null;
  heightPx?: number;
  /** Fill the parent's height instead of `heightPx` — the parent needs a resolved height or this collapses. */
  fill?: boolean;
  className?: string;
};

// ---------------------------------------------------------------------------
// Visual language. One signal colour (Payaca purple) for the traced geometry,
// photographic-neutral panels, everything else stays out of the imagery's way.
// ---------------------------------------------------------------------------

const SIGNAL = '#7743E6';
const SIGNAL_BRIGHT = '#A78BFA';
const PANEL_BASE = '#161D2E';
const PANEL_FRAME = '#9AA0A6';
const PANEL_FRAME_NO_FACES = '#B79CFF';
const PANEL_CELL_LINE = '#8B93A7';
const OBSTRUCTION_COLOUR = '#F04438';

/**
 * String colours, most distinct first: neighbouring strings take the lowest
 * free colour, so the near pairs at the end (orange, teal, violet) rarely meet.
 */
export const SOLAR_STRING_PALETTE = [
  '#3B82F6',
  '#F59E0B',
  '#EC4899',
  '#84CC16',
  '#FACC15',
  '#F97316',
  '#14B8A6',
  '#8B5CF6',
] as const;

/** Palette colour for a string key (cycles); null for an unassigned panel. */
export const solarStringColour = (
  key: number | null | undefined
): string | null => {
  if (key == null || !Number.isInteger(key) || key < 0) return null;
  return SOLAR_STRING_PALETTE[key % SOLAR_STRING_PALETTE.length] ?? null;
};

const STRING_FILL_OPACITY = 0.55;
const FACE_EDIT_DEBOUNCE_MS = 600;

// Staged reveal timings: face outlines trace in, then panels pop in batches.
const PANEL_REVEAL_LEAD_MS = 260;
const PANEL_REVEAL_BATCH = 3;
const PANEL_REVEAL_STEP_MS = 125;
/** Hard ceiling (ms) on the staged reveal's total duration. */
const PANEL_REVEAL_MAX_MS = 1500;

/** Stacked strokes behind the core line fake a neon glow — Maps polygons can't drop-shadow. */
const faceGlowLayers = (editing: boolean) => [
  {
    strokeWeight: editing ? 11 : 9,
    strokeOpacity: editing ? 0.17 : 0.13,
    zIndex: 0,
  },
  {
    strokeWeight: editing ? 6 : 5,
    strokeOpacity: editing ? 0.34 : 0.26,
    zIndex: 0,
  },
];

const PROCESSING_STYLES = `
@keyframes payaca-solar-scan {
  0% { top: -22%; }
  100% { top: 108%; }
}
@keyframes payaca-solar-dash {
  to { stroke-dashoffset: -180; }
}
@keyframes payaca-solar-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}
.payaca-solar-scanband {
  position: absolute;
  left: 0;
  right: 0;
  height: 22%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(167, 139, 250, 0.14) 35%,
    rgba(167, 139, 250, 0.6) 50%,
    rgba(167, 139, 250, 0.14) 65%,
    transparent 100%
  );
  animation: payaca-solar-scan 2.6s linear infinite;
}
.payaca-solar-dashrect {
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  animation: payaca-solar-dash 6s linear infinite;
}
.payaca-solar-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: ${SIGNAL_BRIGHT};
  animation: payaca-solar-dot 1.2s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .payaca-solar-scanband,
  .payaca-solar-dashrect,
  .payaca-solar-dot {
    animation: none;
  }
}
`;

export const SolarRoofView: FC<SolarRoofViewProps> = (props) => {
  const {
    apiKey,
    centre,
    panels,
    roofSegments,
    panelWidthMeters,
    panelHeightMeters,
    activePanelsCount,
    selectedPanelIndexes,
    onPanelClick,
    faces,
    faceEditMode,
    selectedFaceIndex,
    referenceEdge,
    showAllFaceOutlines,
    grabFaces,
    moveWholeRoof,
    onRoofMoved,
    onSelectFace,
    obstructions,
    obstacleEditMode,
    onObstacleRemove,
    selectedObstructionIndex,
    addObstacleMode,
    onObstacleDrawn,
    staticFaceOutlines,
    packedPanels,
    fluxOverlay,
    showFluxOverlay,
    rgbUnderlay,
    showRgbUnderlay,
    underlayAtEveryZoom,
    onOverlayImageError,
    editableFaces,
    onFaceEdited,
    boundaryEditFaceIndex,
    onFaceBoundaryChanged,
    addFaceMode,
    addFaceTemplate = 'outline',
    onTemplateDrawn,
    addFaceAzimuthDegrees,
    fitToFacesKey,
    onFaceDrawn,
    onFaceDrawStart,
    processingLabel,
    repositionMode,
    repositionCentre,
    onRepositionChange,
    markerCentre,
    footprints,
    footprintPickMode,
    onFootprintPick,
    parcelBoundary,
    heightPx = 360,
    fill = false,
    className,
  } = props;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hoveredFaceIndex, setHoveredFaceIndex] = useState<number | null>(null);
  const onPanelClickRef = useRef(onPanelClick);
  onPanelClickRef.current = onPanelClick;
  const onFaceEditedRef = useRef(onFaceEdited);
  onFaceEditedRef.current = onFaceEdited;
  const onSelectFaceRef = useRef(onSelectFace);
  onSelectFaceRef.current = onSelectFace;
  const onFaceBoundaryChangedRef = useRef(onFaceBoundaryChanged);
  onFaceBoundaryChangedRef.current = onFaceBoundaryChanged;
  const onRoofMovedRef = useRef(onRoofMoved);
  onRoofMovedRef.current = onRoofMoved;
  const onFaceMovedRef = useRef(props.onFaceMoved);
  onFaceMovedRef.current = props.onFaceMoved;
  const faceDragPreviewRef = useRef<{
    faceIndex: number;
    delta: { dLat: number; dLng: number };
  } | null>(null);
  const onFaceDrawStartRef = useRef(onFaceDrawStart);
  onFaceDrawStartRef.current = onFaceDrawStart;
  // Kept in a ref so an azimuth-prop change mid-draw doesn't reset progress.
  const drawFirstCornerRef = useRef<SolarLatLng | null>(null);
  const drawCursorRef = useRef<SolarLatLng | null>(null);
  const addFaceAzimuthRef = useRef(addFaceAzimuthDegrees);
  addFaceAzimuthRef.current = addFaceAzimuthDegrees;
  const addFaceTemplateRef = useRef(addFaceTemplate);
  addFaceTemplateRef.current = addFaceTemplate;
  const drawPreviewRef = useRef<(cursor: SolarLatLng) => void>(() => undefined);
  const onFaceDrawnRef = useRef(onFaceDrawn);
  onFaceDrawnRef.current = onFaceDrawn;
  const onTemplateDrawnRef = useRef(onTemplateDrawn);
  onTemplateDrawnRef.current = onTemplateDrawn;
  const onObstacleRemoveRef = useRef(onObstacleRemove);
  onObstacleRemoveRef.current = onObstacleRemove;
  const onObstacleDrawnRef = useRef(onObstacleDrawn);
  onObstacleDrawnRef.current = onObstacleDrawn;
  const onRepositionChangeRef = useRef(onRepositionChange);
  onRepositionChangeRef.current = onRepositionChange;
  const onFootprintPickRef = useRef(onFootprintPick);
  onFootprintPickRef.current = onFootprintPick;

  /** Whether the staged reveal has already run — arms again once faces first arrive. */
  const revealConsumedRef = useRef(false);
  const hadFacesRef = useRef(false);
  /** Resolves once the first satellite tiles have painted. */
  const tilesReadyRef = useRef<Promise<void>>(Promise.resolve());

  const hasFaces = !!faces?.length;
  if (hasFaces && !hadFacesRef.current) {
    revealConsumedRef.current = false;
  }
  hadFacesRef.current = hasFaces;

  const [editingFaceIndexes, setEditingFaceIndexes] = useState<
    ReadonlySet<number>
  >(() => new Set());

  useEffect(() => {
    setEditingFaceIndexes(new Set());
  }, [packedPanels, editableFaces]);

  const renderedPanels = useMemo(() => {
    if (packedPanels) {
      return packedPanels.map((panel) => ({
        corners: panel.corners,
        faceIndex: panel.faceIndex,
      }));
    }
    return panels.map((panel) => ({
      corners: computePanelCorners(
        panel,
        roofSegments[panel.segmentIndex],
        panelWidthMeters,
        panelHeightMeters
      ),
      faceIndex: null as number | null,
    }));
  }, [packedPanels, panels, roofSegments, panelWidthMeters, panelHeightMeters]);

  const activeIndexSet = useMemo(() => {
    if (selectedPanelIndexes) return new Set(selectedPanelIndexes);
    const count = activePanelsCount ?? renderedPanels.length;
    return new Set(renderedPanels.slice(0, count).map((_, i) => i));
  }, [selectedPanelIndexes, activePanelsCount, renderedPanels]);

  const renderedPanelsRef = useRef(renderedPanels);
  renderedPanelsRef.current = renderedPanels;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsScript(apiKey)
      .then(() => {
        if (!cancelled) setIsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  /** Satellite imagery's own zoom ceiling (via MaxZoomService) — the raster map type silently ignores setZoom past it. */
  const satelliteCeilingRef = useRef(20);

  /** Read by map listeners; kept in step with `canDeepZoom` below. */
  const canDeepZoomRef = useRef(false);
  /** Map is past Google's satellite ceiling, on the blank base. */
  const [onBlankBase, setOnBlankBase] = useState(false);

  useEffect(() => {
    if (!isLoaded || !mapContainerRef.current || mapRef.current) return;
    const mapOptions: Record<string, unknown> = {
      center: centre,
      zoom: 20,
      mapTypeId: 'satellite',
      tilt: 0,
      disableDefaultUI: true,
      // Google's zoom control disables itself at the imagery ceiling; we render our own instead.
      zoomControl: false,
      backgroundColor: '#9ca3af',
      gestureHandling: 'greedy',
      isFractionalZoomEnabled: true,
    };
    // Vector/WebGL satellite has gone blank at high zoom; raster keeps tiles through the ceiling.
    if (window.google.maps.RenderingType) {
      mapOptions.renderingType = window.google.maps.RenderingType.RASTER;
    }
    const map = new window.google.maps.Map(mapContainerRef.current, mapOptions);
    map.mapTypes.set(
      BLANK_MAP_TYPE_ID,
      new window.google.maps.ImageMapType({
        getTileUrl: () => null,
        tileSize: new window.google.maps.Size(256, 256),
        maxZoom: MAX_MAP_ZOOM,
        name: 'Blank',
      })
    );
    new window.google.maps.MaxZoomService()
      .getMaxZoomAtLatLng(centre)
      .then((result: { status: string; zoom?: number }) => {
        if (result.status === 'OK' && result.zoom) {
          satelliteCeilingRef.current = result.zoom;
          syncBaseToZoom();
        }
      })
      .catch(() => undefined);
    // Hysteresis avoids flicker at the ceiling boundary.
    const syncBaseToZoom = () => {
      const zoom = map.getZoom();
      if (zoom == null) return;
      const ceiling = satelliteCeilingRef.current;
      const onBlank = map.getMapTypeId() === BLANK_MAP_TYPE_ID;
      if (zoom > ceiling + 0.15) {
        if (canDeepZoomRef.current) {
          if (!onBlank) map.setMapTypeId(BLANK_MAP_TYPE_ID);
        } else if (zoom > ceiling) {
          map.setZoom(ceiling);
          if (onBlank) map.setMapTypeId('satellite');
        }
        return;
      }
      if (zoom <= ceiling - 0.35 && onBlank) {
        map.setMapTypeId('satellite');
      }
    };
    map.addListener('zoom_changed', syncBaseToZoom);
    // The blank base is exactly "above Google's satellite ceiling".
    const syncOnBlankBase = () =>
      setOnBlankBase(map.getMapTypeId() === BLANK_MAP_TYPE_ID);
    map.addListener('maptypeid_changed', syncOnBlankBase);
    map.addListener('zoom_changed', syncOnBlankBase);
    // Google clamps its own wheel gesture at the ceiling — we cross onto the blank base ourselves to keep the zoom moving.
    const container = mapContainerRef.current;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY >= 0) return;
      if (!canDeepZoomRef.current) return;
      const zoom = map.getZoom();
      if (
        zoom == null ||
        zoom < satelliteCeilingRef.current - 0.05 ||
        map.getMapTypeId() !== 'satellite'
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      map.setMapTypeId(BLANK_MAP_TYPE_ID);
      map.setZoom(Math.min(MAX_MAP_ZOOM, zoom + 0.5));
    };
    container.addEventListener('wheel', onWheel, {
      passive: false,
      capture: true,
    });
    mapRef.current = map;
    tilesReadyRef.current = new Promise<void>((resolve) => {
      const listener = map.addListener('tilesloaded', () => {
        listener.remove();
        resolve();
      });
      // Fallback: never hold the reveal hostage to a missing tiles event.
      setTimeout(resolve, 4000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useMapContainerResize(isLoaded, mapContainerRef, mapRef);

  // Declared after the map is created so the overlays mount on the same pass.
  const { rgbReady } = useSolarGroundOverlays({
    isLoaded,
    mapRef,
    rgbUnderlay,
    showRgbUnderlay,
    aboveSatelliteCeiling: onBlankBase,
    underlayAtEveryZoom,
    fluxOverlay,
    showFluxOverlay,
    onImageError: onOverlayImageError,
  });

  /**
   * Past Google's satellite ceiling the base is blank: only offer deep zoom
   * while the analysed imagery is shown AND has actually loaded, so a missing,
   * loading or expired underlay never leaves a bare background.
   */
  const canDeepZoom = rgbReady && !!showRgbUnderlay;
  canDeepZoomRef.current = canDeepZoom;

  /** Last `fitToFacesKey` acted on — face edits alone must never re-trigger the fit and yank the viewport. */
  const fitConsumedKeyRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (fitToFacesKey === undefined) return;
    if (fitToFacesKey === fitConsumedKeyRef.current) return;
    fitConsumedKeyRef.current = fitToFacesKey;
    const bounds = facesBoundingBox(faces ?? [], 0.15);
    if (!bounds) return;
    mapRef.current.fitBounds(
      new window.google.maps.LatLngBounds(bounds.sw, bounds.ne)
    );
  }, [isLoaded, fitToFacesKey, faces]);

  // Face chrome varies by mode: view (none), select, move, static, and legacy vertex-drag.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !faces?.length) return;
    const chromeMode = resolveFaceChromeMode({
      faceEditMode,
      moveWholeRoof,
      editableFaces,
      staticFaceOutlines,
    });
    if (chromeMode === 'none') return;
    const selectMode = chromeMode === 'select';
    const moveMode = chromeMode === 'move';
    const staticMode = chromeMode === 'static';
    const map = mapRef.current;
    const listeners: any[] = [];
    const debounceTimers = new Map<number, ReturnType<typeof setTimeout>>();
    const createdPolygons: any[] = [];

    // ── Static mode: every face drawn as a read-only outline (Obstacles stage).
    // Non-clickable so it never intercepts obstacle add/remove clicks. ──
    if (staticMode) {
      faces.forEach((face) => {
        createdPolygons.push(
          new window.google.maps.Polygon({
            paths: [face.outer, ...face.holes],
            map,
            strokeColor: SIGNAL,
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: SIGNAL,
            fillOpacity: 0.05,
            clickable: false,
            zIndex: 3,
          })
        );
      });
      return () => {
        createdPolygons.forEach((polygon) => polygon.setMap(null));
      };
    }

    const readRings = (polygon: any): SolarLatLng[][] =>
      polygon
        .getPaths()
        .getArray()
        .map((path: any) =>
          path
            .getArray()
            .map((point: any) => ({ lat: point.lat(), lng: point.lng() }))
        );
    const readPolygon = (polygon: any): SolarFacePolygon => {
      const [outer = [], ...holes] = readRings(polygon);
      return { outer, holes };
    };

    // ── Select mode: hit polygon per face below the panels, outline above for
    // the selected/hovered one. The boundary-redraw face is instead an
    // editable polygon. Move mode shares this pass via the grabbable branch. ──
    if (selectMode || moveMode) {
      faces.forEach((face, faceIndex) => {
        const paths = [face.outer, ...face.holes];

        if (!moveWholeRoof && faceIndex === boundaryEditFaceIndex) {
          const editable = new window.google.maps.Polygon({
            paths,
            map,
            strokeColor: SIGNAL,
            strokeOpacity: 1,
            strokeWeight: 4,
            fillColor: SIGNAL,
            fillOpacity: 0.1,
            clickable: true,
            editable: true,
            draggable: true,
            zIndex: 7,
          });
          createdPolygons.push(editable);
          // Test hook (DEV only): lets e2e drivers reshape the boundary programmatically —
          // mutating the path fires the same listeners a real vertex drag does.
          if (import.meta.env.DEV) {
            (window as any).__payacaSolarBoundaryPolygon = editable;
          }
          let movingFace = false;
          const boundaryCommit = boundaryEditCommit(
            map.getDiv(),
            () =>
              onFaceBoundaryChangedRef.current?.(
                faceIndex,
                readPolygon(editable)
              ),
            FACE_EDIT_DEBOUNCE_MS
          );
          listeners.push(boundaryCommit);
          const onPathChanged = () => {
            if (movingFace) return;
            setEditingFaceIndexes((current) =>
              current.has(faceIndex) ? current : new Set(current).add(faceIndex)
            );
            boundaryCommit.changed();
          };
          editable.getPaths().forEach((path: any) => {
            ['set_at', 'insert_at', 'remove_at'].forEach((eventName) => {
              listeners.push(path.addListener(eventName, onPathChanged));
            });
          });
          listeners.push(
            editable.addListener('rightclick', (event: any) => {
              if (event.vertex == null) return;
              const outerPath = editable.getPaths().getAt(0);
              if (outerPath.getLength() > 3) outerPath.removeAt(event.vertex);
            })
          );
          // A drag-ending click is the drag, not a toggle — see `draggedAt` below.
          let draggedAt = 0;
          listeners.push(
            editable.addListener('click', (event: any) => {
              if (event.vertex != null || event.edge != null) return;
              if (Date.now() - draggedAt < 300) return;
              const latLng = event.latLng;
              if (!latLng || !onPanelClickRef.current) return;
              const panelIndex = panelIndexAtPoint(
                renderedPanelsRef.current,
                { lat: latLng.lat(), lng: latLng.lng() },
                faceIndex
              );
              if (panelIndex != null) {
                onPanelClickRef.current(panelIndex, {
                  metaKey: !!event.domEvent?.metaKey,
                  ctrlKey: !!event.domEvent?.ctrlKey,
                  shiftKey: !!event.domEvent?.shiftKey,
                });
              }
            })
          );
          // Native polygon dragging is a translation; vertex edits use the path
          // listeners above and intentionally leave panel positions unchanged.
          const anchor = { ...face.outer[0] };
          listeners.push(
            editable.addListener('dragstart', () => {
              movingFace = true;
              boundaryCommit.cancel();
            }),
            editable.addListener('drag', () => {
              const first = editable.getPath().getAt(0);
              if (!first || !onFaceMovedRef.current) return;
              faceDragPreviewRef.current = {
                faceIndex,
                delta: {
                  dLat: first.lat() - anchor.lat,
                  dLng: first.lng() - anchor.lng,
                },
              };
              syncPanelsRef.current();
            })
          );
          listeners.push(
            editable.addListener('dragend', () => {
              draggedAt = Date.now();
              boundaryCommit.cancel();
              movingFace = false;
              const moved = readPolygon(editable);
              const first = moved.outer[0];
              if (first && onFaceMovedRef.current) {
                onFaceMovedRef.current(faceIndex, {
                  dLat: first.lat - anchor.lat,
                  dLng: first.lng - anchor.lng,
                });
              } else {
                onFaceBoundaryChangedRef.current?.(faceIndex, moved);
              }
              faceDragPreviewRef.current = null;
            })
          );
          return;
        }

        // Grab mode: the outline itself is the drag surface.
        if (moveWholeRoof || (showAllFaceOutlines && grabFaces)) {
          const grabbable = new window.google.maps.Polygon({
            paths,
            map,
            strokeColor: SIGNAL,
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: SIGNAL,
            fillOpacity: 0.05,
            clickable: true,
            draggable: true,
            zIndex: 4,
          });
          createdPolygons.push(grabbable);
          if (!moveWholeRoof) {
            listeners.push(
              grabbable.addListener('click', () =>
                onSelectFaceRef.current?.(faceIndex)
              )
            );
          }
          listeners.push(
            grabbable.addListener('mouseover', () =>
              grabbable.setOptions({ strokeWeight: 4, fillOpacity: 0.09 })
            )
          );
          listeners.push(
            grabbable.addListener('mouseout', () =>
              grabbable.setOptions({ strokeWeight: 3, fillOpacity: 0.05 })
            )
          );
          const anchor = { ...face.outer[0] };
          listeners.push(
            grabbable.addListener('dragstart', () => {
              if (!moveWholeRoof && onFaceMovedRef.current) return;
              setEditingFaceIndexes((current) => {
                const next = new Set(current);
                if (moveWholeRoof) {
                  faces.forEach((_, index) => next.add(index));
                } else {
                  next.add(faceIndex);
                }
                return next;
              });
            })
          );
          listeners.push(
            grabbable.addListener('drag', () => {
              if (moveWholeRoof || !onFaceMovedRef.current) return;
              const first = grabbable.getPath().getAt(0);
              if (!first) return;
              faceDragPreviewRef.current = {
                faceIndex,
                delta: {
                  dLat: first.lat() - anchor.lat,
                  dLng: first.lng() - anchor.lng,
                },
              };
              syncPanelsRef.current();
            })
          );
          listeners.push(
            grabbable.addListener('dragend', () => {
              const moved = readPolygon(grabbable);
              const first = moved.outer[0];
              if (!first) return;
              if (moveWholeRoof) {
                onRoofMovedRef.current?.({
                  dLat: first.lat - anchor.lat,
                  dLng: first.lng - anchor.lng,
                });
                return;
              }
              onSelectFaceRef.current?.(faceIndex);
              if (onFaceMovedRef.current) {
                onFaceMovedRef.current(faceIndex, {
                  dLat: first.lat - anchor.lat,
                  dLng: first.lng - anchor.lng,
                });
              } else {
                onFaceBoundaryChangedRef.current?.(faceIndex, moved);
              }
              faceDragPreviewRef.current = null;
            })
          );
          return;
        }

        const hit = new window.google.maps.Polygon({
          paths,
          map,
          strokeOpacity: 0,
          fillOpacity: 0,
          clickable: true,
          zIndex: 1,
        });
        hit.addListener('click', () => onSelectFaceRef.current?.(faceIndex));
        hit.addListener('mouseover', () => setHoveredFaceIndex(faceIndex));
        hit.addListener('mouseout', () =>
          setHoveredFaceIndex((current) =>
            current === faceIndex ? null : current
          )
        );
        createdPolygons.push(hit);

        const isSelected = faceIndex === selectedFaceIndex;
        const isHovered = faceIndex === hoveredFaceIndex;
        if (isSelected || isHovered) {
          // Non-clickable so it never intercepts panel toggles.
          const outline = new window.google.maps.Polygon({
            paths,
            map,
            strokeColor: SIGNAL,
            strokeOpacity: 1,
            strokeWeight: isSelected ? 4 : 3,
            fillColor: SIGNAL,
            fillOpacity: isSelected ? 0.1 : 0.04,
            clickable: false,
            zIndex: 6,
          });
          createdPolygons.push(outline);
        } else if (showAllFaceOutlines) {
          createdPolygons.push(
            new window.google.maps.Polygon({
              paths,
              map,
              strokeColor: SIGNAL,
              strokeOpacity: 1,
              strokeWeight: 3,
              fillColor: SIGNAL,
              fillOpacity: 0.03,
              clickable: false,
              zIndex: 5,
            })
          );
        }
      });
      return () => {
        listeners.forEach((listener) => listener.remove());
        debounceTimers.forEach((timer) => clearTimeout(timer));
        createdPolygons.forEach((polygon) => polygon.setMap(null));
        if (import.meta.env.DEV) {
          (window as any).__payacaSolarBoundaryPolygon = undefined;
        }
      };
    }

    // ── Legacy vertex-drag mode (server repackSolarPanels path) ──
    faces.forEach((face, faceIndex) => {
      const paths = [face.outer, ...face.holes];
      const glowPolygons = faceGlowLayers(true).map(
        (layer) =>
          new window.google.maps.Polygon({
            paths,
            map,
            strokeColor: SIGNAL,
            strokeOpacity: layer.strokeOpacity,
            strokeWeight: layer.strokeWeight,
            fillOpacity: 0,
            clickable: false,
            zIndex: layer.zIndex,
          })
      );
      const corePolygon = new window.google.maps.Polygon({
        paths,
        map,
        strokeColor: SIGNAL,
        strokeOpacity: 1,
        strokeWeight: 4,
        fillColor: SIGNAL,
        fillOpacity: 0.06,
        clickable: true,
        editable: true,
        zIndex: 1,
      });
      createdPolygons.push(...glowPolygons, corePolygon);
      const onPathChanged = () => {
        const rings = readRings(corePolygon);
        glowPolygons.forEach((glow) => glow.setPaths(rings));
        setEditingFaceIndexes((current) =>
          current.has(faceIndex) ? current : new Set(current).add(faceIndex)
        );
        const pending = debounceTimers.get(faceIndex);
        if (pending) clearTimeout(pending);
        debounceTimers.set(
          faceIndex,
          setTimeout(() => {
            debounceTimers.delete(faceIndex);
            onFaceEditedRef.current?.(faceIndex, readPolygon(corePolygon));
          }, FACE_EDIT_DEBOUNCE_MS)
        );
      };
      corePolygon.getPaths().forEach((path: any) => {
        ['set_at', 'insert_at', 'remove_at'].forEach((eventName) => {
          listeners.push(path.addListener(eventName, onPathChanged));
        });
      });
    });

    return () => {
      listeners.forEach((listener) => listener.remove());
      debounceTimers.forEach((timer) => clearTimeout(timer));
      createdPolygons.forEach((polygon) => polygon.setMap(null));
    };
  }, [
    isLoaded,
    faces,
    faceEditMode,
    editableFaces,
    staticFaceOutlines,
    selectedFaceIndex,
    hoveredFaceIndex,
    boundaryEditFaceIndex,
    showAllFaceOutlines,
    grabFaces,
    moveWholeRoof,
  ]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !referenceEdge) return;
    const line = new window.google.maps.Polyline({
      map: mapRef.current,
      path: referenceEdge,
      strokeColor: '#f59e0b',
      strokeWeight: 6,
      strokeOpacity: 1,
      clickable: false,
      zIndex: 9,
    });
    return () => line.setMap(null);
  }, [isLoaded, referenceEdge]);

  // Outline drawing stays open until the user clicks its screen-space start target.
  // Azimuth lives in a ref so a DSM sample doesn't tear down the click session.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !addFaceMode) {
      drawFirstCornerRef.current = null;
      drawCursorRef.current = null;
      return;
    }
    const map = mapRef.current;
    let corners: SolarLatLng[] = [];
    const projectionOverlay = new window.google.maps.OverlayView();
    projectionOverlay.onAdd = () => undefined;
    projectionOverlay.draw = () => undefined;
    projectionOverlay.onRemove = () => undefined;
    projectionOverlay.setMap(map);
    const screenPoint = (point: SolarLatLng) =>
      projectionOverlay
        .getProjection()
        ?.fromLatLngToContainerPixel(
          new window.google.maps.LatLng(point.lat, point.lng)
        );
    const outlineAction = (cursor: SolarLatLng) => {
      const projected = corners.map(screenPoint);
      const next = screenPoint(cursor);
      return next && projected.every(Boolean)
        ? roofOutlineAction(projected, next)
        : 'ignore';
    };
    let previews: any[] = [];
    let previewPanels: any[] = [];
    let lastFillAt = 0;

    const clearPreview = () => {
      previews.forEach((outline) => outline.setMap(null));
      previews = [];
      previewPanels.forEach((panel) => panel.setMap(null));
      previewPanels = [];
    };

    const renderPreview = (cursor: SolarLatLng) => {
      const firstCorner = drawFirstCornerRef.current;
      if (!firstCorner) return;
      const azimuth = addFaceAzimuthRef.current ?? 180;
      const template = addFaceTemplateRef.current ?? 'outline';
      if (template === 'outline') {
        const closing = outlineAction(cursor) === 'close';
        if (!previews.length) {
          previews.push(
            new window.google.maps.Polyline({
              map,
              strokeColor: SIGNAL,
              strokeOpacity: 1,
              strokeWeight: 3,
              clickable: false,
              zIndex: 8,
            })
          );
          previews.push(
            new window.google.maps.Marker({
              map,
              position: firstCorner,
              clickable: false,
              zIndex: 9,
              title: 'Click the first corner to close the roof outline',
            })
          );
        }
        previews[0].setPath([...corners, closing ? firstCorner : cursor]);
        previews[1].setIcon({
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: closing ? 9 : 6,
          strokeColor: SIGNAL_BRIGHT,
          strokeWeight: 2,
          fillColor: SIGNAL,
          fillOpacity: closing ? 1 : 0.25,
        });
        return;
      }
      const templateFaces = buildRoofTemplate(
        firstCorner,
        cursor,
        azimuth,
        template
      ).faces;
      while (previews.length < templateFaces.length) {
        previews.push(
          new window.google.maps.Polygon({
            map,
            strokeColor: SIGNAL,
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: SIGNAL,
            fillOpacity: 0.08,
            clickable: false,
            zIndex: 8,
          })
        );
      }
      while (previews.length > templateFaces.length) {
        previews.pop().setMap(null);
      }
      templateFaces.forEach((face, index) =>
        previews[index].setPaths([face.polygon.outer])
      );

      // Throttled — the preview pitch is nominal; the real pack after commit uses the sampled plane.
      const now = Date.now();
      if (now - lastFillAt < 120) return;
      lastFillAt = now;
      previewPanels.forEach((panel) => panel.setMap(null));
      previewPanels = templateFaces
        .flatMap((face) =>
          packFace({
            face: face.polygon,
            pitchDegrees: 30,
            rowAzimuthDegrees: face.azimuthDegrees,
            obstructions: obstructions ?? [],
            panelWidthMeters,
            panelHeightMeters,
            faceIndex: -1,
            segmentIndex: -1,
            energyPerPanelKwh: 0,
          })
        )
        .slice(0, 200)
        .map(
          (panel) =>
            new window.google.maps.Polygon({
              paths: [panel.corners],
              map,
              strokeColor: '#FFFFFF',
              strokeOpacity: 0.7,
              strokeWeight: 1,
              fillColor: PANEL_BASE,
              fillOpacity: 0.45,
              clickable: false,
              zIndex: 9,
            })
        );
    };

    const clickListener = map.addListener('click', (event: any) => {
      if (!event.latLng) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      const template = addFaceTemplateRef.current ?? 'outline';
      if (template === 'outline') {
        const action = outlineAction(point);
        if (action === 'ignore') return;
        if (action === 'close') {
          const polygon = { outer: [...corners], holes: [] };
          corners = [];
          drawFirstCornerRef.current = null;
          drawCursorRef.current = null;
          clearPreview();
          onFaceDrawnRef.current?.(polygon);
        } else {
          corners.push(point);
          if (corners.length === 1) {
            drawFirstCornerRef.current = point;
            onFaceDrawStartRef.current?.(point);
          }
          drawCursorRef.current = point;
          renderPreview(point);
        }
        return;
      }
      if (!drawFirstCornerRef.current) {
        drawFirstCornerRef.current = point;
        onFaceDrawStartRef.current?.(point);
        return;
      }
      const firstCorner = drawFirstCornerRef.current;
      drawFirstCornerRef.current = null;
      drawCursorRef.current = null;
      clearPreview();
      const azimuth = addFaceAzimuthRef.current ?? 180;
      if (template === 'rect') {
        onFaceDrawnRef.current?.(
          buildOrientedRectangle(firstCorner, point, azimuth)
        );
        return;
      }
      onTemplateDrawnRef.current?.(
        buildRoofTemplate(firstCorner, point, azimuth, template)
      );
    });

    const moveListener = map.addListener('mousemove', (event: any) => {
      if (!drawFirstCornerRef.current || !event.latLng) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      drawCursorRef.current = point;
      renderPreview(point);
    });

    drawPreviewRef.current = renderPreview;
    if (drawFirstCornerRef.current && drawCursorRef.current) {
      renderPreview(drawCursorRef.current);
    }

    map.setOptions({ draggableCursor: 'crosshair' });
    const cancelOutline = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      corners = [];
      drawFirstCornerRef.current = null;
      drawCursorRef.current = null;
      clearPreview();
    };
    document.addEventListener('keydown', cancelOutline);
    return () => {
      document.removeEventListener('keydown', cancelOutline);
      clickListener.remove();
      moveListener.remove();
      drawFirstCornerRef.current = null;
      drawCursorRef.current = null;
      drawPreviewRef.current = () => undefined;
      clearPreview();
      projectionOverlay.setMap(null);
      map.setOptions({ draggableCursor: null });
    };
  }, [
    isLoaded,
    addFaceMode,
    addFaceTemplate,
    obstructions,
    panelWidthMeters,
    panelHeightMeters,
  ]);

  useEffect(() => {
    if (!drawFirstCornerRef.current || !drawCursorRef.current) return;
    drawPreviewRef.current(drawCursorRef.current);
  }, [addFaceAzimuthDegrees, addFaceTemplate]);

  // Obstruction zones: subtle tint while editing, bold + clickable-to-remove in the Obstacles stage.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !obstructions?.length) return;
    const editable = !!obstacleEditMode;
    // Stay visible during move, or the model appears to lose them.
    const subtle =
      !editable &&
      (!!editableFaces || faceEditMode === 'select' || !!moveWholeRoof);
    if (!editable && !subtle) return;
    const map = mapRef.current;
    const obstructionPolygons = obstructions.map((ring, index) => {
      const selected = index === selectedObstructionIndex;
      let strokeWeight = 1.25;
      if (selected) strokeWeight = 3;
      else if (editable) strokeWeight = 2;
      let fillOpacity = 0.08;
      if (selected) fillOpacity = 0.4;
      else if (editable) fillOpacity = 0.3;
      const polygon = new window.google.maps.Polygon({
        paths: ring,
        map,
        strokeColor: OBSTRUCTION_COLOUR,
        strokeOpacity: editable || selected ? 0.95 : 0.6,
        strokeWeight,
        fillColor: OBSTRUCTION_COLOUR,
        fillOpacity,
        clickable: editable,
        zIndex: editable ? 6 : 2,
      });
      if (editable) {
        polygon.addListener('click', () =>
          onObstacleRemoveRef.current?.(index)
        );
      }
      return polygon;
    });
    return () => {
      obstructionPolygons.forEach((polygon) => polygon.setMap(null));
    };
  }, [
    isLoaded,
    obstructions,
    obstacleEditMode,
    editableFaces,
    faceEditMode,
    moveWholeRoof,
    selectedObstructionIndex,
  ]);

  // "Add obstacle" draw mode: two clicks define an axis-aligned rectangle.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !addObstacleMode) return;
    const map = mapRef.current;
    let firstCorner: SolarLatLng | null = null;
    let preview: any = null;

    const clearPreview = () => {
      if (preview) {
        preview.setMap(null);
        preview = null;
      }
    };

    const clickListener = map.addListener('click', (event: any) => {
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      if (!firstCorner) {
        firstCorner = point;
        return;
      }
      // Axis-aligned box (azimuth 0): obstacles don't follow the roof slope.
      const ring = buildOrientedRectangle(firstCorner, point, 0).outer;
      firstCorner = null;
      clearPreview();
      onObstacleDrawnRef.current?.(ring);
    });

    const moveListener = map.addListener('mousemove', (event: any) => {
      if (!firstCorner) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      const ring = buildOrientedRectangle(firstCorner, point, 0).outer;
      if (!preview) {
        preview = new window.google.maps.Polygon({
          map,
          strokeColor: OBSTRUCTION_COLOUR,
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: OBSTRUCTION_COLOUR,
          fillOpacity: 0.25,
          clickable: false,
          zIndex: 8,
        });
      }
      preview.setPaths([ring]);
    });

    map.setOptions({ draggableCursor: 'crosshair' });
    return () => {
      clickListener.remove();
      moveListener.remove();
      clearPreview();
      map.setOptions({ draggableCursor: null });
    };
  }, [isLoaded, addObstacleMode]);

  /** Deep-zoom level to restore when the underlay comes back — otherwise toggling it strands the user zoomed out. */
  const deepZoomToRestoreRef = useRef<number | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!isLoaded || !map) return;
    if (!canDeepZoom) {
      if (map.getMapTypeId() === BLANK_MAP_TYPE_ID) {
        deepZoomToRestoreRef.current = map.getZoom() ?? null;
        map.setMapTypeId('satellite');
        map.setZoom(Math.min(map.getZoom() ?? 20, satelliteCeilingRef.current));
      }
      return;
    }
    const restore = deepZoomToRestoreRef.current;
    deepZoomToRestoreRef.current = null;
    if (restore != null && restore > satelliteCeilingRef.current) {
      map.setMapTypeId(BLANK_MAP_TYPE_ID);
      map.setZoom(Math.min(MAX_MAP_ZOOM, restore));
    }
  }, [isLoaded, canDeepZoom]);

  // "Reposition" mode: a draggable pin reports the corrected point.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !repositionMode || !repositionCentre) {
      return;
    }
    const map = mapRef.current;
    map.panTo(repositionCentre);
    const marker = new window.google.maps.Marker({
      position: repositionCentre,
      map,
      draggable: true,
      cursor: 'grab',
      icon: {
        path: 'M12 0C7 0 3 4 3 9c0 6.5 9 15 9 15s9-8.5 9-15c0-5-4-9-9-9z',
        fillColor: SIGNAL,
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
        scale: 1.6,
        anchor: new window.google.maps.Point(12, 24),
      },
      zIndex: 20,
    });
    const emit = (event: any) =>
      onRepositionChangeRef.current?.({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    const dragListener = marker.addListener('dragend', emit);
    const clickListener = map.addListener('click', (event: any) => {
      marker.setPosition(event.latLng);
      emit(event);
    });
    map.setOptions({ draggableCursor: 'crosshair' });
    return () => {
      dragListener.remove();
      clickListener.remove();
      marker.setMap(null);
      map.setOptions({ draggableCursor: null });
    };
  }, [isLoaded, repositionMode, repositionCentre]);

  // Read-only location pin — reposition mode owns the pin instead.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || repositionMode || !markerCentre) {
      return;
    }
    const marker = new window.google.maps.Marker({
      position: markerCentre,
      map: mapRef.current,
      clickable: false,
      icon: {
        path: 'M12 0C7 0 3 4 3 9c0 6.5 9 15 9 15s9-8.5 9-15c0-5-4-9-9-9z',
        fillColor: SIGNAL,
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
        scale: 1.6,
        anchor: new window.google.maps.Point(12, 24),
      },
      zIndex: 20,
    });
    return () => {
      marker.setMap(null);
    };
  }, [isLoaded, repositionMode, markerCentre]);

  // OSM footprints: dashed via Polyline (Maps polygons can't dash a stroke);
  // clickable highlight in `footprintPickMode`.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !footprints?.length) return;
    const map = mapRef.current;
    const overlays: any[] = [];

    if (footprintPickMode) {
      footprints.forEach((ring) => {
        const polygon = new window.google.maps.Polygon({
          paths: ring,
          map,
          strokeColor: BUILDING_OUTLINE_STYLE.colour,
          strokeOpacity: 1,
          strokeWeight: 2.5,
          fillColor: BUILDING_OUTLINE_STYLE.colour,
          fillOpacity: 0.18,
          clickable: true,
          zIndex: 9,
        });
        polygon.addListener('click', () => onFootprintPickRef.current?.(ring));
        polygon.addListener('mouseover', () =>
          polygon.setOptions({ fillOpacity: 0.32 })
        );
        polygon.addListener('mouseout', () =>
          polygon.setOptions({ fillOpacity: 0.18 })
        );
        overlays.push(polygon);
      });
      map.setOptions({ draggableCursor: 'pointer' });
      return () => {
        overlays.forEach((overlay) => overlay.setMap(null));
        map.setOptions({ draggableCursor: null });
      };
    }

    footprints.forEach((ring) => {
      overlays.push(
        new window.google.maps.Polyline({
          path: [...ring, ring[0]],
          map,
          strokeOpacity: 0,
          icons: dashedPolylineIcons(BUILDING_OUTLINE_STYLE),
          clickable: false,
          zIndex: 0,
        })
      );
    });
    return () => {
      overlays.forEach((overlay) => overlay.setMap(null));
    };
  }, [isLoaded, footprints, footprintPickMode]);

  // Title-parcel boundary: dashed gold, above OSM footprints, under all roof chrome.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !parcelBoundary?.length) return;
    const line = new window.google.maps.Polyline({
      path: [...parcelBoundary, parcelBoundary[0]],
      map: mapRef.current,
      strokeOpacity: 0,
      icons: dashedPolylineIcons(TITLE_BOUNDARY_STYLE),
      clickable: false,
      zIndex: 2,
    });
    return () => {
      line.setMap(null);
    };
  }, [isLoaded, parcelBoundary]);

  const facesCount = faces?.length ?? 0;
  const panelStateRef = useRef({
    props,
    renderedPanels,
    activeIndexSet,
    editingFaceIndexes,
    facesCount,
  });
  panelStateRef.current = {
    props,
    renderedPanels,
    activeIndexSet,
    editingFaceIndexes,
    facesCount,
  };
  const syncPanelsRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const map = mapRef.current;
    type PanelOverlay = {
      polygon: any;
      grid: any[];
      listeners: any[];
      outline?: any;
    };
    const overlays = new Map<number, PanelOverlay>();
    const revealTimers: Array<ReturnType<typeof setTimeout>> = [];
    let cancelled = false;
    let revealing = false;
    const revealedIndexes = new Set<number>();
    let suppressClick = false;
    let drag: {
      index: number;
      corners: Map<number, SolarLatLng[]>;
      roof: SolarLatLng[];
      snapAnchors: SolarLatLng[][];
      snapEnabled: boolean;
      panelGapM: number;
      columnGapM?: number;
      rowGapM?: number;
      arrayAzimuthDegrees?: number | null;
      roofPlane?: { pitchDegrees: number; azimuthDegrees: number };
      preview: any;
    } | null = null;
    const container = mapContainerRef.current;
    const onPointerDown = () => {
      suppressClick = false;
    };
    container?.addEventListener('pointerdown', onPointerDown, true);

    let hoveredPanel: number | null = null;

    const removeOverlay = (overlay: PanelOverlay, index: number) => {
      if (hoveredPanel === index) hoveredPanel = null;
      overlay.listeners.forEach((listener) => listener.remove());
      overlay.polygon.setMap(null);
      overlay.grid.forEach((line) => line.setMap(null));
      overlay.outline?.setMap(null);
    };

    const previewDrag = () => {
      if (!drag) return;
      const origin = drag.corners.get(drag.index)?.[0];
      const first = overlays.get(drag.index)?.polygon.getPath().getAt(0);
      if (!origin || !first) return;
      const rawDelta = {
        lat: first.lat() - origin.lat,
        lng: first.lng() - origin.lng,
      };
      // Keep the attraction distance visually consistent as the map zooms.
      const metresPerPixel =
        (156543.03392804097 * Math.cos((origin.lat * Math.PI) / 180)) /
        2 ** (map.getZoom() ?? 20);
      const snapped = drag.snapEnabled
        ? snapPanelDrag({
            selectedPanelCorners: [...drag.corners.values()],
            stationaryPanelCorners: drag.snapAnchors,
            delta: rawDelta,
            panelGapM: drag.panelGapM,
            columnGapM: drag.columnGapM,
            rowGapM: drag.rowGapM,
            arrayAzimuthDegrees: drag.arrayAzimuthDegrees,
            roofPlane: drag.roofPlane,
            snapThresholdM: 8 * metresPerPixel,
          })
        : rawDelta;
      const delta = { dLat: snapped.lat, dLng: snapped.lng };
      const removedPanelIndexes: number[] = [];
      const roof = drag.roof;
      drag.corners.forEach((corners, index) => {
        const translated = corners.map(({ lat, lng }) => ({
          lat: lat + delta.dLat,
          lng: lng + delta.dLng,
        }));
        const withinRoof = isPanelWithinRoof(translated, roof);
        if (!withinRoof) removedPanelIndexes.push(index);
        const overlay = overlays.get(index);
        if (!overlay) return;
        // Keep the invisible native target attached even when its preview is
        // outside, so the same gesture can bring the whole selection back in.
        const preview = index === drag?.index ? drag.preview : overlay.polygon;
        preview.setPath(translated);
        preview.setVisible(withinRoof);
        overlay.outline?.setPath([...translated, translated[0]]);
        overlay.outline?.setVisible(withinRoof);
        computePanelCellGridLines(translated).forEach((path, lineIndex) => {
          overlay.grid[lineIndex]?.setPath(path);
          overlay.grid[lineIndex]?.setVisible(withinRoof);
        });
      });
      return { delta, removedPanelIndexes };
    };

    const addPanel = (index: number) => {
      const polygon = new window.google.maps.Polygon({ map });
      const overlay: PanelOverlay = { polygon, grid: [], listeners: [] };
      overlays.set(index, overlay);
      overlay.listeners.push(
        polygon.addListener('mousedown', () => {
          suppressClick = false;
        }),
        polygon.addListener('mouseover', () => {
          if (drag) return;
          hoveredPanel = index;
          syncPanelsRef.current();
        }),
        polygon.addListener('mouseout', () => {
          if (hoveredPanel !== index) return;
          hoveredPanel = null;
          syncPanelsRef.current();
        }),
        polygon.addListener('click', (event: any) => {
          if (suppressClick || drag) return;
          panelStateRef.current.props.onPanelClick?.(index, {
            metaKey: !!event.domEvent?.metaKey,
            ctrlKey: !!event.domEvent?.ctrlKey,
            shiftKey: !!event.domEvent?.shiftKey,
          });
        }),
        polygon.addListener('dragstart', () => {
          const state = panelStateRef.current;
          const indexes = state.props.draggablePanelIndexes ?? [];
          const group = indexes.includes(index) ? indexes : [index];
          const faceIndex = state.renderedPanels[index]?.faceIndex;
          const roof =
            faceIndex == null
              ? []
              : (state.props.faces?.[faceIndex]?.outer ?? []);
          drag = {
            index,
            roof,
            snapEnabled: !!state.props.snapPanelsToArray,
            panelGapM: state.props.panelGapMeters ?? 0.02,
            columnGapM:
              (faceIndex == null
                ? undefined
                : state.props.snapPanelGaps?.[faceIndex]?.columnGapM) ??
              state.props.columnGapMeters,
            rowGapM:
              (faceIndex == null
                ? undefined
                : state.props.snapPanelGaps?.[faceIndex]?.rowGapM) ??
              state.props.rowGapMeters,
            arrayAzimuthDegrees:
              faceIndex == null
                ? undefined
                : state.props.snapArrayAzimuthDegrees?.[faceIndex],
            roofPlane:
              faceIndex == null
                ? undefined
                : state.props.snapRoofPlanes?.[faceIndex],
            snapAnchors: state.renderedPanels.flatMap((panel, panelIndex) =>
              panel.faceIndex === faceIndex && !group.includes(panelIndex)
                ? [panel.corners.map((corner) => ({ ...corner }))]
                : []
            ),
            preview: new window.google.maps.Polygon({
              map,
              paths: state.renderedPanels[index]?.corners,
              fillColor: PANEL_BASE,
              fillOpacity: state.activeIndexSet.has(index) ? 0.82 : 0.12,
              strokeColor: SIGNAL_BRIGHT,
              strokeOpacity: 1,
              strokeWeight: 3,
              clickable: false,
              zIndex: 6,
            }),
            corners: new Map(
              group.flatMap((panelIndex) => {
                const panel = state.renderedPanels[panelIndex];
                return panel
                  ? [
                      [
                        panelIndex,
                        panel.corners.map((corner) => ({ ...corner })),
                      ] as const,
                    ]
                  : [];
              })
            ),
          };
          suppressClick = true;
          group.forEach((panelIndex) =>
            overlays.get(panelIndex)?.polygon.setOptions({
              strokeColor: SIGNAL_BRIGHT,
              strokeOpacity: 1,
              strokeWeight: 3,
              zIndex: 6,
            })
          );
          polygon.setOptions({ fillOpacity: 0, strokeOpacity: 0 });
        }),
        polygon.addListener('drag', () => {
          previewDrag();
        }),
        polygon.addListener('dragend', () => {
          if (!drag) return;
          const result = previewDrag();
          drag.preview.setMap(null);
          drag = null;
          suppressClick = true;
          if (
            result &&
            (result.delta.dLat !== 0 ||
              result.delta.dLng !== 0 ||
              result.removedPanelIndexes.length > 0)
          ) {
            panelStateRef.current.props.onPanelsMoved?.(
              result.delta,
              result.removedPanelIndexes
            );
          }
          syncPanelsRef.current();
        })
      );
      return overlay;
    };

    const sync = () => {
      if (cancelled || drag) return;
      const state = panelStateRef.current;
      const current = state.props;
      const highlighted = new Set(current.highlightedPanelIndexes);
      const stringHighlighted = new Set(current.stringHighlightPanelIndexes);
      const stringHovered = new Set(current.stringHoverPanelIndexes);
      const draggable = new Set(current.draggablePanelIndexes);
      const interactive =
        !current.moveWholeRoof &&
        !current.addFaceMode &&
        !current.addObstacleMode &&
        !current.repositionMode &&
        !current.footprintPickMode;
      const canMove = interactive && !!current.onPanelsMoved;
      const clickable = interactive && (!!current.onPanelClick || canMove);
      const drawable = state.renderedPanels
        .map((panel, index) => ({ panel, index }))
        .filter(({ panel, index }) => {
          if (
            !state.activeIndexSet.has(index) &&
            !clickable &&
            !current.packedPanels
          )
            return false;
          return (
            panel.faceIndex == null ||
            !state.editingFaceIndexes.has(panel.faceIndex)
          );
        });
      const visible = new Set(drawable.map(({ index }) => index));
      overlays.forEach((overlay, index) => {
        if (visible.has(index)) return;
        removeOverlay(overlay, index);
        overlays.delete(index);
      });
      const hoveredFace =
        hoveredPanel == null
          ? undefined
          : state.renderedPanels[hoveredPanel]?.faceIndex;
      const hoversWholeRoof =
        hoveredFace != null &&
        ![...highlighted].some(
          (index) => state.renderedPanels[index]?.faceIndex === hoveredFace
        );
      const hoverPreviews = (index: number) =>
        hoveredPanel === index ||
        (hoversWholeRoof &&
          state.renderedPanels[index]?.faceIndex === hoveredFace);
      const update = (index: number) => {
        if (cancelled || drag) return;
        const latest = panelStateRef.current;
        const panel = latest.renderedPanels[index];
        if (!panel) return;
        const overlay = overlays.get(index) ?? addPanel(index);
        const active = latest.activeIndexSet.has(index);
        const selected = highlighted.has(index);
        const inHighlightedString = stringHighlighted.has(index);
        const inHoveredString =
          !inHighlightedString && stringHovered.has(index);
        const faceDrag = faceDragPreviewRef.current;
        const corners =
          faceDrag?.faceIndex === panel.faceIndex
            ? panel.corners.map(({ lat, lng }) => ({
                lat: lat + faceDrag.delta.dLat,
                lng: lng + faceDrag.delta.dLng,
              }))
            : panel.corners;
        const restingZIndex = active ? 4 : 3;
        const stringColour = solarStringColour(
          latest.props.panelStringKeys?.[index]
        );
        overlay.polygon.setOptions({
          visible: true,
          paths: corners,
          fillColor: PANEL_BASE,
          fillOpacity: active ? 0.82 : 0.12,
          strokeColor:
            active && !state.facesCount ? PANEL_FRAME_NO_FACES : PANEL_FRAME,
          strokeOpacity: active ? 0.85 : 0.6,
          strokeWeight: active ? 1.5 : 1,
          // A string colours the whole panel; the cell grid stays on top.
          ...(stringColour
            ? {
                fillColor:
                  inHighlightedString || inHoveredString
                    ? tinycolor
                        .mix(
                          stringColour,
                          '#FFFFFF',
                          inHighlightedString ? 20 : 10
                        )
                        .toHexString()
                    : stringColour,
                fillOpacity: STRING_FILL_OPACITY,
                strokeColor: stringColour,
                strokeOpacity: 1,
                strokeWeight: 2,
              }
            : {}),
          clickable,
          draggable: canMove && draggable.has(index),
          // Roof grab surfaces sit at 4. Every interactive panel must remain
          // above them, including unselected panels in an additive selection.
          zIndex: clickable ? 5 : restingZIndex,
          ...(selected || (clickable && hoverPreviews(index))
            ? {
                strokeColor: stringColour
                  ? SELECTED_PANELS_OUTLINE_STYLE.colour
                  : SIGNAL_BRIGHT,
                strokeOpacity: selected
                  ? SELECTED_PANELS_OUTLINE_STYLE.opacity
                  : 0.5,
                strokeWeight: SELECTED_PANELS_OUTLINE_STYLE.weightPx,
                zIndex: 6,
              }
            : {}),
        });
        if (inHighlightedString || inHoveredString) {
          overlay.outline ??= new window.google.maps.Polyline({
            map,
            strokeOpacity: 0,
            clickable: false,
            zIndex: 7,
          });
          overlay.outline.setOptions({
            path: [...corners, corners[0]],
            icons: dashedPolylineIcons({
              ...HIGHLIGHTED_STRING_OUTLINE_STYLE,
              opacity: inHighlightedString ? 1 : 0.5,
            }),
            visible: true,
          });
        } else {
          overlay.outline?.setMap(null);
          overlay.outline = undefined;
        }
        const lines =
          active || stringColour ? computePanelCellGridLines(corners) : [];
        while (overlay.grid.length > lines.length)
          overlay.grid.pop().setMap(null);
        lines.forEach((path, lineIndex) => {
          if (!overlay.grid[lineIndex]) {
            overlay.grid.push(
              new window.google.maps.Polyline({
                map,
                strokeColor: PANEL_CELL_LINE,
                strokeOpacity: 0.3,
                strokeWeight: 1,
                clickable: false,
                zIndex: 5,
              })
            );
          }
          // Darker, firmer cell lines read through a coloured string fill.
          overlay.grid[lineIndex].setOptions(
            stringColour
              ? { strokeColor: PANEL_BASE, strokeOpacity: 0.45 }
              : { strokeColor: PANEL_CELL_LINE, strokeOpacity: 0.3 }
          );
          overlay.grid[lineIndex].setPath(path);
          overlay.grid[lineIndex].setVisible(true);
        });
      };

      if (!revealConsumedRef.current && drawable.length && !revealing) {
        revealing = true;
        void tilesReadyRef.current.then(() => {
          if (cancelled) return;
          revealConsumedRef.current = true;
          const batchSize = Math.max(
            PANEL_REVEAL_BATCH,
            Math.ceil(
              drawable.length / (PANEL_REVEAL_MAX_MS / PANEL_REVEAL_STEP_MS)
            )
          );
          for (let order = 0; order < drawable.length; order += batchSize) {
            revealTimers.push(
              setTimeout(
                () => {
                  drawable
                    .slice(order, order + batchSize)
                    .forEach(({ index }) => revealedIndexes.add(index));
                  if (order + batchSize >= drawable.length) revealing = false;
                  // Reconcile from current props, including changes while tiles were loading.
                  syncPanelsRef.current();
                },
                (state.facesCount ? PANEL_REVEAL_LEAD_MS : 0) +
                  Math.floor(order / batchSize) * PANEL_REVEAL_STEP_MS
              )
            );
          }
        });
        return;
      }
      drawable.forEach(({ index }) => {
        if (!revealing || revealedIndexes.has(index)) update(index);
      });
    };
    syncPanelsRef.current = sync;

    return () => {
      cancelled = true;
      drag?.preview.setMap(null);
      container?.removeEventListener('pointerdown', onPointerDown, true);
      syncPanelsRef.current = () => undefined;
      revealTimers.forEach((timer) => clearTimeout(timer));
      overlays.forEach(removeOverlay);
    };
  }, [isLoaded]);

  useEffect(() => {
    syncPanelsRef.current();
  });

  type StringLabelEntry = {
    corners: SolarLatLng[];
    label: string;
    colour: string;
    panelIndex: number;
  };
  const stringLabelsControllerRef = useRef<{
    setLabels: (labels: StringLabelEntry[]) => void;
  } | null>(null);

  // One lightweight overlay holds every string pill, independent of the panel
  // polygons so a label change never touches them.
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const map = mapRef.current;
    const overlay = new window.google.maps.OverlayView();
    const container = document.createElement('div');
    container.setAttribute('data-testid', 'solar-string-labels');
    Object.assign(container.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      pointerEvents: 'none',
    });
    let entries: Array<{ corners: SolarLatLng[]; element: HTMLElement }> = [];

    overlay.onAdd = () => {
      overlay.getPanes?.()?.floatPane?.appendChild(container);
    };
    overlay.draw = () => {
      const projection = overlay.getProjection?.();
      const zoom = map.getZoom?.();
      // Below Google's satellite ceiling the panels are too small to label.
      const visible =
        !!projection && zoom != null && zoom >= satelliteCeilingRef.current;
      container.style.display = visible ? '' : 'none';
      if (!visible) return;
      entries.forEach(({ corners, element }) => {
        const clickable = !!onStringLabelClickRef.current;
        element.style.pointerEvents = clickable ? 'auto' : 'none';
        element.style.cursor = clickable ? 'pointer' : '';
        const points = corners.map((corner) =>
          projection.fromLatLngToDivPixel(
            new window.google.maps.LatLng(corner.lat, corner.lng)
          )
        );
        if (!points.length || points.some((point: unknown) => !point)) {
          element.style.display = 'none';
          return;
        }
        const xs = points.map((point: { x: number }) => point.x);
        const ys = points.map((point: { y: number }) => point.y);
        element.style.display = '';
        element.style.left = `${(Math.min(...xs) + Math.max(...xs)) / 2}px`;
        element.style.top = `${Math.min(...ys)}px`;
      });
    };
    overlay.onRemove = () => {
      container.remove();
    };
    overlay.setMap(map);

    const createPill = () => {
      const element = document.createElement('div');
      element.setAttribute('data-testid', 'solar-string-label');
      window.google.maps.OverlayView.preventMapHitsAndGesturesFrom(element);
      Object.assign(element.style, {
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '1px 7px 1px 5px',
        borderRadius: '9999px',
        borderWidth: '2px',
        borderStyle: 'solid',
        background: '#FFFFFF',
        color: '#1F2433',
        fontSize: '11px',
        fontWeight: '600',
        lineHeight: '16px',
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.35)',
        pointerEvents: 'none',
        userSelect: 'none',
      });
      element.addEventListener('mouseenter', () => {
        const panelIndex = Number(element.dataset.panelIndex);
        if (Number.isFinite(panelIndex))
          onStringLabelHoverRef.current?.(panelIndex);
      });
      element.addEventListener('mouseleave', () =>
        onStringLabelHoverRef.current?.(null)
      );
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        const panelIndex = Number(element.dataset.panelIndex);
        if (Number.isFinite(panelIndex))
          onStringLabelClickRef.current?.(panelIndex);
      });
      const dot = document.createElement('span');
      Object.assign(dot.style, {
        width: '7px',
        height: '7px',
        borderRadius: '9999px',
        flex: 'none',
      });
      const text = document.createElement('span');
      element.append(dot, text);
      container.appendChild(element);
      return element;
    };

    stringLabelsControllerRef.current = {
      setLabels: (labels) => {
        while (entries.length > labels.length) entries.pop()?.element.remove();
        entries = labels.map((entry, index) => {
          const element = entries[index]?.element ?? createPill();
          const [dot, text] = Array.from(element.children) as HTMLElement[];
          element.style.borderColor = entry.colour;
          element.dataset.panelIndex = String(entry.panelIndex);
          if (dot) dot.style.background = entry.colour;
          if (text) text.textContent = entry.label;
          return { corners: entry.corners, element };
        });
        overlay.draw();
      },
    };
    const zoomListener = map.addListener?.('zoom_changed', () =>
      overlay.draw()
    );

    return () => {
      stringLabelsControllerRef.current = null;
      zoomListener?.remove();
      overlay.setMap(null);
      container.remove();
    };
  }, [isLoaded]);

  const { stringLabels, panelStringKeys } = props;
  const onStringLabelClickRef = useRef(props.onStringLabelClick);
  onStringLabelClickRef.current = props.onStringLabelClick;
  const onStringLabelHoverRef = useRef(props.onStringLabelHover);
  onStringLabelHoverRef.current = props.onStringLabelHover;
  const stringLabelsClickable = !!props.onStringLabelClick;
  useEffect(() => {
    const labels = (stringLabels ?? []).flatMap(({ panelIndex, label }) => {
      const corners = renderedPanels[panelIndex]?.corners;
      if (!corners?.length) return [];
      const colour = solarStringColour(panelStringKeys?.[panelIndex]) ?? SIGNAL;
      return [{ corners, label, colour, panelIndex }];
    });
    stringLabelsControllerRef.current?.setLabels(labels);
  }, [
    isLoaded,
    stringLabels,
    panelStringKeys,
    renderedPanels,
    stringLabelsClickable,
  ]);

  if (loadError) {
    return (
      <div
        className={className}
        style={{
          height: fill ? '100%' : heightPx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span>Unable to load the roof view</span>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        height: fill ? '100%' : heightPx,
        width: '100%',
        borderRadius: fill ? 0 : 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={mapContainerRef}
        data-testid="solar-roof-view"
        style={{ position: 'absolute', inset: 0 }}
      />
      {isLoaded && (
        <div
          style={{
            position: 'absolute',
            right: 12,
            bottom: 52,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          {(
            [
              ['Zoom in', '+', 1],
              ['Zoom out', '−', -1],
            ] as const
          ).map(([label, glyph, delta]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              onClick={() => {
                const map = mapRef.current;
                if (!map) return;
                const zoom = map.getZoom() ?? 20;
                const ceiling = satelliteCeilingRef.current;
                const maxZoom = canDeepZoomRef.current ? MAX_MAP_ZOOM : ceiling;
                const target = Math.min(
                  maxZoom,
                  Math.max(10, Math.round(zoom) + delta)
                );
                if (target > ceiling) {
                  if (map.getMapTypeId() !== BLANK_MAP_TYPE_ID) {
                    map.setMapTypeId(BLANK_MAP_TYPE_ID);
                  }
                } else if (map.getMapTypeId() === BLANK_MAP_TYPE_ID) {
                  map.setMapTypeId('satellite');
                }
                map.setZoom(target);
              }}
              style={{
                width: 36,
                height: 36,
                border: 'none',
                borderTop: delta < 0 ? '1px solid #e5e2ee' : 'none',
                background: '#fff',
                color: '#3c3651',
                fontSize: 20,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              {glyph}
            </button>
          ))}
        </div>
      )}
      {processingLabel && (
        <>
          <style>{PROCESSING_STYLES}</style>
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              overflow: 'hidden',
              background: 'rgba(10, 13, 23, 0.45)',
              pointerEvents: 'none',
            }}
          >
            <div className="payaca-solar-scanband" />
            <svg
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <rect
                className="payaca-solar-dashrect"
                x="4"
                y="4"
                rx="10"
                fill="none"
                stroke={SIGNAL_BRIGHT}
                strokeWidth="2"
                strokeDasharray="12 8"
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 14px',
                  borderRadius: 9999,
                  background: 'rgba(13, 16, 28, 0.78)',
                  color: '#F4F1FF',
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: 0.2,
                }}
              >
                <span className="payaca-solar-dot" />
                {processingLabel}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
